/**
 * useMemberReportTemplateEditor — the state and actions of MemberReportTemplateEditor, shared by the legacy render and MemberReportTemplateEditorNext.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../../services/api.js';
import {
  tokensToDisplayHtml,
  displayHtmlToTokens,
  tokensToHebrewPreview,
  defaultTemplateForType,
} from './memberReportMergeFields.js';
import {
  DEFAULT_LETTER_INSETS,
  normalizeLetterInsets,
} from './memberPrint.js';
import { ckConfirm } from '@NP-Webber/crm-kit/dist/next/index.js';

export const TARGET_TYPE_LABELS = { list: 'רשימה', breakdown: 'פילוח', card: 'כרטיס נבחן' };

export const INSET_FIELDS = [
  { key: 'topMm', label: 'עליון', max: 120 },
  { key: 'rightMm', label: 'ימין', max: 80 },
  { key: 'bottomMm', label: 'תחתון', max: 80 },
  { key: 'leftMm', label: 'שמאל', max: 80 },
];

export function useMemberReportTemplateEditor(props) {
  const {
    open,
    onClose,
    initialTargetType = 'list',
  } = props;
  const editorRef = useRef(null);
  const fileRef = useRef(null);
  const initialLoadedRef = useRef(false);
  const blankUrlRef = useRef('');
  const insetsSaveTimer = useRef(null);

  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [name, setName] = useState('תבנית חדשה');
  const [targetType, setTargetType] = useState(initialTargetType || 'list');
  const [bodyHtml, setBodyHtml] = useState(defaultTemplateForType(initialTargetType || 'list'));
  const [previewHtml, setPreviewHtml] = useState('');
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [blankMeta, setBlankMeta] = useState(null);
  const [blankPreviewUrl, setBlankPreviewUrl] = useState('');
  const [letterInsets, setLetterInsets] = useState({ ...DEFAULT_LETTER_INSETS });
  const [insetsDirty, setInsetsDirty] = useState(false);
  const [savingInsets, setSavingInsets] = useState(false);

  const editorDisplayHtml = useMemo(() => tokensToDisplayHtml(bodyHtml), [bodyHtml]);

  const revokeBlankUrl = useCallback(() => {
    if (blankUrlRef.current) {
      URL.revokeObjectURL(blankUrlRef.current);
      blankUrlRef.current = '';
    }
  }, []);

  const syncFromEditor = () => {
    if (!editorRef.current) return;
    setBodyHtml(displayHtmlToTokens(editorRef.current.innerHTML));
  };

  const loadBlankImage = useCallback(async () => {
    try {
      const response = await api.get('/member-reports/blank/download', { responseType: 'blob' });
      revokeBlankUrl();
      const url = URL.createObjectURL(response.data);
      blankUrlRef.current = url;
      setBlankPreviewUrl(url);
    } catch {
      revokeBlankUrl();
      setBlankPreviewUrl('');
    }
  }, [revokeBlankUrl]);

  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await api.get('/member-reports/templates');
      const list = data.data || [];
      setTemplates(list);
      const match = list.find((t) => t.targetType === (initialTargetType || 'list')) || list[0];
      if (match && !initialLoadedRef.current) {
        initialLoadedRef.current = true;
        setSelectedId(String(match._id));
        setName(match.name);
        setTargetType(match.targetType);
        setBodyHtml(match.bodyHtml || defaultTemplateForType(match.targetType));
      }
    } catch {
      setTemplates([]);
    }
  }, [initialTargetType]);

  const loadBlank = useCallback(async () => {
    try {
      const { data } = await api.get('/member-reports/blank');
      const meta = data.data;
      setBlankMeta(meta);
      if (meta) {
        setLetterInsets(normalizeLetterInsets(meta.letterInsets));
        setInsetsDirty(false);
        await loadBlankImage();
      } else {
        setLetterInsets({ ...DEFAULT_LETTER_INSETS });
        setInsetsDirty(false);
        revokeBlankUrl();
        setBlankPreviewUrl('');
      }
    } catch {
      setBlankMeta(null);
      revokeBlankUrl();
      setBlankPreviewUrl('');
    }
  }, [loadBlankImage, revokeBlankUrl]);

  useEffect(() => {
    if (!open) return undefined;
    initialLoadedRef.current = false;
    setTargetType(initialTargetType || 'list');
    setBodyHtml(defaultTemplateForType(initialTargetType || 'list'));
    setFeedback('');
    loadTemplates();
    loadBlank();
    return () => {
      if (insetsSaveTimer.current) clearTimeout(insetsSaveTimer.current);
      revokeBlankUrl();
    };
  }, [open, initialTargetType, loadTemplates, loadBlank, revokeBlankUrl]);

  useEffect(() => {
    if (!open || !bodyHtml) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post('/member-reports/templates/preview-merge', {
          bodyHtml,
          targetType,
        });
        if (!cancelled) setPreviewHtml(data.data?.html || tokensToHebrewPreview(bodyHtml));
      } catch {
        if (!cancelled) setPreviewHtml(tokensToHebrewPreview(bodyHtml));
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, bodyHtml, targetType]);

  useEffect(() => {
    if (!open || !editorRef.current) return;
    if (editorRef.current.innerHTML !== editorDisplayHtml) {
      editorRef.current.innerHTML = editorDisplayHtml;
    }
  }, [open, editorDisplayHtml, selectedId]);

  const saveInsets = useCallback(async (nextInsets) => {
    if (!blankMeta) return;
    setSavingInsets(true);
    try {
      const { data } = await api.patch('/member-reports/blank', {
        letterInsets: normalizeLetterInsets(nextInsets),
      });
      setBlankMeta(data.data);
      setLetterInsets(normalizeLetterInsets(data.data?.letterInsets));
      setInsetsDirty(false);
    } catch (err) {
      setFeedback(err?.response?.data?.error || 'שגיאה בשמירת מרווחים');
    } finally {
      setSavingInsets(false);
    }
  }, [blankMeta]);

  const scheduleInsetsSave = useCallback((next) => {
    setLetterInsets(next);
    setInsetsDirty(true);
    if (insetsSaveTimer.current) clearTimeout(insetsSaveTimer.current);
    insetsSaveTimer.current = setTimeout(() => {
      saveInsets(next);
    }, 700);
  }, [saveInsets]);

  const handleInsetChange = (key, value) => {
    const next = normalizeLetterInsets({ ...letterInsets, [key]: value });
    scheduleInsetsSave(next);
  };

  const insertHtml = (html) => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (sel && sel.rangeCount) {
      const range = sel.getRangeAt(0);
      if (el.contains(range.commonAncestorContainer)) {
        range.deleteContents();
        const temp = document.createElement('div');
        temp.innerHTML = html;
        const frag = document.createDocumentFragment();
        while (temp.firstChild) frag.appendChild(temp.firstChild);
        range.insertNode(frag);
        syncFromEditor();
        return;
      }
    }
    el.innerHTML = `${el.innerHTML}${html}`;
    syncFromEditor();
  };

  const selectTemplate = (tpl) => {
    setSelectedId(String(tpl._id));
    setName(tpl.name);
    setTargetType(tpl.targetType);
    setBodyHtml(tpl.bodyHtml || defaultTemplateForType(tpl.targetType));
  };

  const handleNew = () => {
    setSelectedId('');
    setName('תבנית חדשה');
    setTargetType(initialTargetType || 'list');
    setBodyHtml(defaultTemplateForType(initialTargetType || 'list'));
  };

  const handleSave = async () => {
    syncFromEditor();
    const html = editorRef.current
      ? displayHtmlToTokens(editorRef.current.innerHTML)
      : bodyHtml;
    if (!name.trim()) {
      setFeedback('שם תבנית חובה');
      return;
    }
    setLoading(true);
    setFeedback('');
    try {
      if (selectedId) {
        const { data } = await api.put(`/member-reports/templates/${selectedId}`, {
          name: name.trim(),
          targetType,
          bodyHtml: html,
        });
        setBodyHtml(html);
        setFeedback('התבנית נשמרה');
        await loadTemplates();
        if (data.data?._id) setSelectedId(String(data.data._id));
      } else {
        const { data } = await api.post('/member-reports/templates', {
          name: name.trim(),
          targetType,
          bodyHtml: html,
        });
        setBodyHtml(html);
        setSelectedId(String(data.data._id));
        setFeedback('התבנית נוצרה');
        await loadTemplates();
      }
      if (insetsDirty && blankMeta) {
        await saveInsets(letterInsets);
      }
    } catch (err) {
      setFeedback(err?.response?.data?.error || 'שגיאה בשמירה');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    if (!(await ckConfirm({ title: 'מחיקת תבנית', message: 'למחוק את התבנית?', confirmLabel: 'מחיקה' }))) return;
    setLoading(true);
    try {
      await api.delete(`/member-reports/templates/${selectedId}`);
      handleNew();
      await loadTemplates();
      setFeedback('התבנית נמחקה');
    } catch (err) {
      setFeedback(err?.response?.data?.error || 'שגיאה במחיקה');
    } finally {
      setLoading(false);
    }
  };

  const handleBlankUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', 'רקע מכתב');
    setLoading(true);
    try {
      const { data } = await api.post('/member-reports/blank', fd);
      setBlankMeta(data.data);
      setLetterInsets(normalizeLetterInsets(data.data?.letterInsets));
      setInsetsDirty(false);
      await loadBlankImage();
      setFeedback('רקע המכתב הועלה — כוונו את המרווחים עד שהטקסט יושב נכון');
    } catch (err) {
      setFeedback(err?.response?.data?.error || 'שגיאה בהעלאת רקע');
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleBlankRemove = async () => {
    setLoading(true);
    try {
      await api.delete('/member-reports/blank');
      setBlankMeta(null);
      setLetterInsets({ ...DEFAULT_LETTER_INSETS });
      setInsetsDirty(false);
      revokeBlankUrl();
      setBlankPreviewUrl('');
      setFeedback('רקע המכתב הוסר');
    } catch (err) {
      setFeedback(err?.response?.data?.error || 'שגיאה בהסרת רקע');
    } finally {
      setLoading(false);
    }
  };

  const hasBlank = Boolean(blankMeta && blankPreviewUrl);
  const previewPageClass = hasBlank ? 'letter-page has-blank' : 'letter-page';
  const previewBgStyle = hasBlank
    ? { backgroundImage: `url('${blankPreviewUrl}')` }
    : undefined;

  return {
    templates,
    setTemplates,
    selectedId,
    setSelectedId,
    name,
    setName,
    targetType,
    setTargetType,
    bodyHtml,
    setBodyHtml,
    previewHtml,
    setPreviewHtml,
    feedback,
    setFeedback,
    loading,
    setLoading,
    blankMeta,
    setBlankMeta,
    blankPreviewUrl,
    setBlankPreviewUrl,
    letterInsets,
    setLetterInsets,
    insetsDirty,
    setInsetsDirty,
    savingInsets,
    setSavingInsets,
    editorRef,
    fileRef,
    initialLoadedRef,
    blankUrlRef,
    insetsSaveTimer,
    editorDisplayHtml,
    revokeBlankUrl,
    syncFromEditor,
    loadBlankImage,
    loadTemplates,
    loadBlank,
    saveInsets,
    scheduleInsetsSave,
    handleInsetChange,
    insertHtml,
    selectTemplate,
    handleNew,
    handleSave,
    handleDelete,
    handleBlankUpload,
    handleBlankRemove,
    hasBlank,
    previewPageClass,
    previewBgStyle,
    open,
    onClose,
    initialTargetType,
  };
}
